import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useSafeMode, AI_PERSONALITIES } from '../contexts/SafeModeContext';
import { User, Bell, Building2, Globe, Users, Briefcase, Upload, Loader2, Zap, Power, Brain, Mail, CreditCard, Sparkles, GitBranch, UserPlus, AtSign, AlertTriangle, Download, Trash2, Crown, Plus, ArrowRight, Clock, Compass, ChevronDown, FileText, Plug, Key, RotateCcw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { cn } from '../lib/utils';
import { notificationsApi, organizationsApi, billingApi, stripeApi, supportFormsApi, productsApi } from '../services/api';
import { useUpgradeReturn } from '../hooks/useUpgradeReturn';
import AssignSeatsModal from '../components/billing/AssignSeatsModal';
import BillingAuditLog from '../components/billing/BillingAuditLog';
import BillingPricingGrid from '../components/billing/BillingPricingGrid';
import PaymentMethodManagement from '../components/billing/PaymentMethodManagement';
import TokenManagement from '../components/TokenManagement';
import UsageManagement from '../components/UsageManagement';
import { OrgNavigationSettings } from '../components/settings/NavigationSettings';
import { useFeatureFlags, FEATURE_IDS } from '../hooks/useFeatureFlags';
import OrgIntegrationsSection from '../components/settings/OrgIntegrationsSection';
import ConnectedAccountsSection from '../components/settings/ConnectedAccountsSection';
import DeveloperKeysTab from '../components/settings/DeveloperKeysTab';
import { API_BASE_URL } from '../config/api';
import TeamPage from './TeamPage';

const BACKEND_URL = API_BASE_URL;

const INDUSTRIES = [
  'SaaS / Software',
  'E-commerce / Retail',
  'Fintech / Finance',
  'Healthcare / Biotech',
  'Education / EdTech',
  'Media / Entertainment',
  'Manufacturing',
  'Professional Services',
  'Other'
];

const TEAM_SIZES = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-20', label: '6-20 people' },
  { value: '20+', label: '20+ people' }
];

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = (() => {
    const tab = searchParams.get('tab') || 'experience';
    return tab === 'notifications' ? 'profile' : tab;
  })();
  
  const { user, organization, refreshAuth } = useAuth();
  const isFreeProduct = organization?.account_type === 'free_product';
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const { 
    safeMode, 
    toggleSafeMode, 
    personality, 
    setPersonality, 
    getActivePersonality,
    getThemeColors 
  } = useSafeMode();
  
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [tokenRefreshKey, setTokenRefreshKey] = useState(0);
  
  // Organization form state
  const [orgForm, setOrgForm] = useState({
    name: '',
    company_website: '',
    industry: '',
    team_size: '',
    timezone: 'America/New_York'
  });
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    email_subscription_alerts: true,
    email_ai_complete: true,
    email_kanban_updates: false,
    email_feature_assigned: true,
    email_feature_unassigned: false,
    email_mentions: true
  });
  
  // Danger Zone state
  const [closureStatus, setClosureStatus] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeConfirmName, setCloseConfirmName] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [closingOrg, setClosingOrg] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Start Over state (free tier only)
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [startingOver, setStartingOver] = useState(false);
  
  // User profile state
  const [userTimezone, setUserTimezone] = useState(user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [availableTimezones, setAvailableTimezones] = useState({ common: [], all: [] });
  const [profileName, setProfileName] = useState(user?.name || '');
  
  // Collapsible sections state
  const [companyInfoExpanded, setCompanyInfoExpanded] = useState(true);
  const [navigationExpanded, setNavigationExpanded] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Seat management state
  const [seatLimitModalOpen, setSeatLimitModalOpen] = useState(false);
  const [seatInfo, setSeatInfo] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [purchasingSeats, setPurchasingSeats] = useState(false);
  
  // Billing history state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeSeats, setUpgradeSeats] = useState(1);
  const [startingUpgrade, setStartingUpgrade] = useState(false);
  const [loadingUpgradeInfo, setLoadingUpgradeInfo] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);

  // Handle post-checkout return and seat assignment
  const {
    showAssignSeats,
    setShowAssignSeats,
    newSeatsCount,
    isProcessing: isProcessingCheckout
  } = useUpgradeReturn({
    onUpgradeSuccess: () => {
      // Refresh seat info after successful upgrade
      fetchSeatInfo();
      refreshAuth();
    },
    onSeatsAvailable: (seats) => {
      console.log('[SettingsPage] Seats available to assign:', seats);
    },
    checkPendingRequests: true,
  });

  // Fetch seat info for paid users
  const fetchSeatInfo = async () => {
    if (organization?.plan_tier !== 'paid' && organization?.plan_tier !== 'pro') return;
    
    setLoadingSeats(true);
    try {
      const [seatRes, requestsRes] = await Promise.all([
        stripeApi.getSeatStatus(),
        billingApi.getPendingUpgradeRequests()
      ]);
      setSeatInfo(seatRes.data);
      setPendingRequests(requestsRes.data?.requests || []);
    } catch (err) {
      console.error('Failed to load seat info:', err);
    } finally {
      setLoadingSeats(false);
    }
  };

  // Fetch available timezones
  const fetchTimezones = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/timezones`);
      const data = await response.json();
      setAvailableTimezones(data);
    } catch (err) {
      console.error('Failed to load timezones:', err);
    }
  };

  // Initialize user profile state
  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      // Use user's saved timezone, or detect from browser
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(user.timezone || browserTimezone || 'UTC');
    }
  }, [user]);

  // Fetch timezones on mount
  useEffect(() => {
    fetchTimezones();
  }, []);

  // Initialize form with organization data
  useEffect(() => {
    if (organization) {
      setOrgForm({
        name: organization.name || '',
        company_website: organization.company_website || '',
        industry: organization.industry || '',
        team_size: organization.team_size || '',
        timezone: organization.timezone || 'America/New_York'
      });
      
      // Load seat info for paid orgs
      if (organization.plan_tier === 'paid' || organization.plan_tier === 'pro') {
        fetchSeatInfo();
      }
    }
  }, [organization]);
  
  // Load closure status
  useEffect(() => {
    const loadClosureStatus = async () => {
      try {
        const response = await organizationsApi.getClosureStatus();
        setClosureStatus(response.data);
      } catch (err) {
        console.error('Failed to load closure status:', err);
      }
    };
    loadClosureStatus();
  }, []);
  
  // Load seat info and pending requests when on billing tab
  useEffect(() => {
    const loadSeatInfo = async () => {
      if (defaultTab !== 'billing') return;
      
      setLoadingSeats(true);
      try {
        // Get upgrade context which includes seat info
        const contextResponse = await billingApi.getUpgradeContext('seats');
        const context = contextResponse.data;
        
        setSeatInfo({
          purchased_seats: context?.seats_purchased || 0,
          used_seats: context?.used_seats || 0,
          available_seats: context?.available_seats || 0,
          seat_price: context?.seat_price || 0,
        });
        
        // Get pending upgrade requests
        const requestsResponse = await billingApi.getPendingUpgradeRequests();
        setPendingRequests(requestsResponse.data?.requests || []);
        
      } catch (err) {
        console.error('Failed to load seat info:', err);
      } finally {
        setLoadingSeats(false);
      }
    };
    
    loadSeatInfo();
  }, [defaultTab]);
  
  // Load billing history when on billing tab
  useEffect(() => {
    const loadBillingHistory = async () => {
      if (defaultTab !== 'billing') return;
      
      setLoadingInvoices(true);
      try {
        const [invoicesRes, paymentMethodsRes, upcomingRes] = await Promise.all([
          stripeApi.getInvoices(),
          stripeApi.getPaymentMethods(),
          stripeApi.getUpcomingInvoice()
        ]);
        
        setInvoices(invoicesRes.data?.invoices || []);
        setPaymentMethods(paymentMethodsRes.data?.payment_methods || []);
        setUpcomingInvoice(upcomingRes.data?.upcoming || null);
      } catch (err) {
        console.error('Failed to load billing history:', err);
      } finally {
        setLoadingInvoices(false);
      }
    };
    
    loadBillingHistory();
  }, [defaultTab]);
  
  // Load notification preferences
  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await notificationsApi.getPreferences();
        if (res.data.preferences) {
          setNotifPrefs(prev => ({ ...prev, ...res.data.preferences }));
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
      }
    };
    loadNotifPrefs();
  }, []);

  // Handle purchasing additional seats
  const handlePurchaseSeats = async (quantity = 1) => {
    setPurchasingSeats(true);
    try {
      // Create checkout session for seat purchase
      const successUrl = `${window.location.origin}/settings?tab=billing&checkout=success&seats_added=${quantity}`;
      const cancelUrl = `${window.location.origin}/settings?tab=billing&checkout=cancelled`;
      
      // plan_id is required - get from organization
      if (!organization?.plan_id) {
        toast.error('No plan found. Please contact support.');
        return;
      }
      
      const response = await stripeApi.createCheckoutSession({
        seats: quantity,
        plan_id: organization.plan_id,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Failed to purchase seats:', err);
      toast.error(err.response?.data?.detail || 'Failed to initiate seat purchase');
    } finally {
      setPurchasingSeats(false);
    }
  };
  
  // Handle starting the upgrade flow (for free tier users)
  const handleStartUpgrade = async () => {
    setStartingUpgrade(true);
    try {
      const successUrl = `${window.location.origin}/settings?tab=billing&checkout=success&seats_added=${upgradeSeats}&upgraded=true`;
      const cancelUrl = `${window.location.origin}/settings?tab=billing&checkout=cancelled`;
      
      // plan_id is required - get from organization
      if (!organization?.plan_id) {
        toast.error('No plan found. Please contact support.');
        return;
      }
      
      const response = await stripeApi.createCheckoutSession({
        seats: upgradeSeats,
        plan_id: organization.plan_id,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Failed to start upgrade:', err);
      toast.error(err.response?.data?.detail || 'Failed to start upgrade');
    } finally {
      setStartingUpgrade(false);
    }
  };
  
  // Load upgrade info when opening the modal
  const loadUpgradeInfo = async () => {
    setLoadingUpgradeInfo(true);
    try {
      // Get seat status, limits, and pending requests to determine recommended seats
      const [seatRes, limitsRes, requestsRes] = await Promise.all([
        stripeApi.getSeatStatus(),
        billingApi.getLimits(),
        billingApi.getPendingUpgradeRequests()
      ]);
      
      // Use new field names with fallbacks for backwards compatibility
      const usedSeats = seatRes.data?.seats_occupied || seatRes.data?.used_seats || 1;
      const purchasedSeats = seatRes.data?.seats_purchased || 0;
      const seatsComped = seatRes.data?.seats_comped || 0;
      const seatsTotal = seatRes.data?.seats_total || (purchasedSeats + seatsComped);
      const planSeatLimit = seatRes.data?.plan_seat_limit || null;
      
      const planTier = organization?.plan_tier;
      const isAlreadyPaid = planTier === 'paid' || planTier === 'pro';
      const pendingCount = requestsRes.data?.requests?.length || 0;
      
      // Get plan features from limits or use defaults
      const limits = limitsRes.data || {};
      
      setUpgradeInfo({
        usedSeats,
        purchasedSeats,
        isAlreadyPaid,
        pendingRequests: pendingCount,
        pricePerSeat: seatRes.data?.price_per_seat || 0,
        planSeatLimit,
        seatsComped,
        seatsTotal,
        planFeatures: {
          maxProducts: limits.max_products === null ? 'Unlimited' : (limits.max_products || 'Unlimited'),
          maxFeatures: limits.max_features_per_product === null ? 'Unlimited' : (limits.max_features_per_product || 'Unlimited'),
          maxEpics: limits.max_epics_per_product === null ? 'Unlimited' : (limits.max_epics_per_product || 'Unlimited'),
          pmSeatsAllowed: limits.pm_seats_allowed !== false,
          billingAdminAllowed: limits.billing_admin_allowed !== false,
        }
      });
      
      // Set recommended seats based on current usage + pending requests
      if (isAlreadyPaid) {
        // Adding to existing subscription - default to pending requests or 1
        setUpgradeSeats(Math.max(1, pendingCount));
      } else {
        // New upgrade - recommend enough for current paid roles + pending requests
        const recommendedSeats = usedSeats + pendingCount;
        setUpgradeSeats(Math.max(1, recommendedSeats));
      }
    } catch (err) {
      console.error('Failed to load upgrade info:', err);
      // Set defaults
      setUpgradeInfo({
        usedSeats: 1,
        purchasedSeats: 0,
        isAlreadyPaid: false,
        pendingRequests: 0,
        pricePerSeat: 29,
        planSeatLimit: null,
        seatsComped: 0,
        seatsTotal: 0,
        planFeatures: {
          maxProducts: 'Unlimited',
          maxFeatures: 'Unlimited',
          maxEpics: 'Unlimited',
          pmSeatsAllowed: true,
          billingAdminAllowed: true,
        }
      });
      setUpgradeSeats(1);
    } finally {
      setLoadingUpgradeInfo(false);
    }
  };
  
  // Open upgrade modal and load info
  const handleOpenUpgradeModal = () => {
    setShowUpgradeModal(true);
    loadUpgradeInfo();
  };
  
  // Handle resolving an upgrade request
  const handleResolveRequest = async (requestId) => {
    try {
      await billingApi.dismissUpgradeRequest(requestId);
      setPendingRequests(prev => prev.filter(r => r.request_id !== requestId));
      toast.success('Request marked as resolved');
    } catch (err) {
      console.error('Failed to resolve request:', err);
    }
  };

  const handleSafeModeToggle = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      toggleSafeMode();
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 150);
    
    if (safeMode) {
      toast.success('Safe Mode disabled', {
        description: 'Welcome to the dark side'
      });
    } else {
      toast.success('Safe Mode enabled', {
        description: 'Corporate white activated'
      });
    }
  };

  const handlePersonalityChange = (newPersonality) => {
    setPersonality(newPersonality);
    const selected = AI_PERSONALITIES[newPersonality];
    toast.success(`AI Personality: ${selected.name}`, {
      description: selected.preview
    });
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          name: profileName.trim(),
          timezone: userTimezone 
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update profile');
      }
      
      await refreshAuth();
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error('Failed to save profile:', err);
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveNotificationPrefs = async () => {
    setSavingNotifications(true);
    try {
      await notificationsApi.updatePreferences(notifPrefs);
      toast.success('Notification preferences saved');
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  };
  
  const handleNotifPrefChange = (key, value) => {
    setNotifPrefs(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const { presignedUpload } = await import('../services/upload');
      const result = await presignedUpload(file, {
        organizationId: organization.organization_id,
        entityType: 'branding',
        entityId: organization.organization_id,
        isPublic: true,
      });
      const logoUrl = `/api/files/${result.file_id}/public`;
      await fetch(`${BACKEND_URL}/api/organizations/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ logo_url: logoUrl })
      });
      await refreshAuth();
      toast.success('Logo updated');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const { presignedUpload } = await import('../services/upload');
      const result = await presignedUpload(file, {
        organizationId: organization?.organization_id,
        entityType: 'avatar',
        entityId: user?.user_id,
        isPublic: true,
      });
      const avatarUrl = `/api/files/${result.file_id}/public`;
      await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ avatar_url: avatarUrl })
      });
      await refreshAuth();
      toast.success('Avatar updated');
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSaveOrganization = async () => {
    if (!orgForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizations/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: orgForm.name,
          company_website: orgForm.company_website || null,
          industry: orgForm.industry,
          team_size: orgForm.team_size,
          timezone: orgForm.timezone
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update organization');
      }

      await refreshAuth();
      toast.success('Organization settings saved');
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Failed to save organization settings');
    } finally {
      setSaving(false);
    }
  };

  // Danger Zone handlers
  const handleCloseOrganization = async () => {
    if (closeConfirmName.toLowerCase().trim() !== organization?.name?.toLowerCase().trim()) {
      toast.error('Organization name does not match');
      return;
    }
    
    setClosingOrg(true);
    try {
      const response = await organizationsApi.closeOrganization(closeConfirmName, cancelImmediately);
      toast.success('Organization scheduled for closure', {
        description: response.data.message
      });
      setShowCloseModal(false);
      setCloseConfirmName('');
      // Reload closure status
      const statusRes = await organizationsApi.getClosureStatus();
      setClosureStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to close organization:', err);
      toast.error(err.response?.data?.detail || 'Failed to close organization');
    } finally {
      setClosingOrg(false);
    }
  };

  const handleReactivateOrganization = async () => {
    setReactivating(true);
    try {
      await organizationsApi.reactivateOrganization();
      toast.success('Organization reactivated!');
      const statusRes = await organizationsApi.getClosureStatus();
      setClosureStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to reactivate:', err);
      toast.error(err.response?.data?.detail || 'Failed to reactivate organization');
    } finally {
      setReactivating(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await organizationsApi.exportData();
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${organization?.name || 'organization'}-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (err) {
      console.error('Failed to export data:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Start Over: archive the user's product and redirect to wizard
  const handleStartOver = async () => {
    setStartingOver(true);
    try {
      // Find the user's active product
      const productsRes = await productsApi.list(false);
      const activeProducts = productsRes.data?.products || productsRes.data || [];
      
      if (activeProducts.length > 0) {
        // Archive each active product (should be just 1 for free tier)
        for (const product of activeProducts) {
          await productsApi.archive(product.product_id);
        }
      }
      
      toast.success('Product archived. Starting fresh!');
      setShowStartOverModal(false);
      navigate('/products/new');
    } catch (err) {
      console.error('Failed to start over:', err);
      toast.error('Failed to archive product. Please try again.');
    } finally {
      setStartingOver(false);
    }
  };

  const isSystemAdmin = user?.role === 'admin';
  const isOrgOwner = user?.role === 'owner' || user?.teamRole === 'owner';
  const isBillingAdmin = user?.teamRole === 'billing_admin';
  const canEditOrganization = isSystemAdmin || isOrgOwner || isBillingAdmin;  // Billing admins can also edit org settings
  const canAccessDangerZone = isSystemAdmin || isOrgOwner;
  const activePersonality = getActivePersonality();
  const currentUserRole = user?.teamRole || 'collaborator';

  // Get selectable AI personalities (non-safe mode)
  const selectablePersonalities = Object.values(AI_PERSONALITIES).filter(p => !p.safeMode);

  return (
    <div className={cn(
      "flex h-screen transition-all duration-300",
      colors.bgPrimary,
      isTransitioning && 'opacity-50'
    )}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Settings" />
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue={isBillingAdmin ? 'billing' : defaultTab} className="space-y-6">
              <TabsList className={cn(
                "border p-1 h-auto gap-1",
                colors.bgSecondary,
                colors.borderPrimary
              )}>
                {/* Experience tab - hidden for billing_admin */}
                {!isBillingAdmin && (
                  <TabsTrigger 
                    value="experience" 
                    className={cn(
                      "data-[state=active]:shadow-sm transition-all",
                      colors.tabActive
                    )}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Experience
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="profile" 
                  className={cn(
                    "data-[state=active]:shadow-sm transition-all",
                    colors.tabActive
                  )}
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </TabsTrigger>
                <TabsTrigger 
                  value="organization" 
                  className={cn(
                    "data-[state=active]:shadow-sm transition-all",
                    colors.tabActive
                  )}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Organization
                </TabsTrigger>
                {/* Integrations tab - only for owners and system admins, hidden if no non-MCP integrations enabled */}
                {(isSystemAdmin || isOrgOwner) && (
                  isEnabled(FEATURE_IDS.INTEGRATION_GITHUB) || 
                  isEnabled(FEATURE_IDS.INTEGRATION_JIRA) || 
                  isEnabled(FEATURE_IDS.INTEGRATION_LINEAR) ||
                  isEnabled(FEATURE_IDS.INTEGRATION_ADO)
                ) ? (
                  <TabsTrigger 
                    value="integrations" 
                    className={cn(
                      "data-[state=active]:shadow-sm transition-all",
                      colors.tabActive
                    )}
                  >
                    <Plug className="w-4 h-4 mr-2" />
                    Integrations
                  </TabsTrigger>
                ) : null}
                {isEnabled(FEATURE_IDS.ORG_TEAM) && (
                  <TabsTrigger 
                    value="team" 
                    className={cn(
                      "data-[state=active]:shadow-sm transition-all",
                      colors.tabActive
                    )}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Team
                  </TabsTrigger>
                )}
                {/* API Keys tab - hidden for billing_admin */}
                {!isBillingAdmin && (
                  <TabsTrigger 
                    value="api-keys" 
                    className={cn(
                      "data-[state=active]:shadow-sm transition-all",
                      colors.tabActive
                    )}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    API Keys
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="billing" 
                  className={cn(
                    "data-[state=active]:shadow-sm transition-all",
                    colors.tabActive,
                    !(isOrgOwner || isBillingAdmin) && "hidden"
                  )}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </TabsTrigger>
              </TabsList>

              {/* Experience Tab - Combined Appearance & AI Personality */}
              <TabsContent value="experience">
                <div className="space-y-6">
                  {/* Safe Mode Toggle Card */}
                  <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500",
                          safeMode ? 'bg-green-100' : colors.bgActive
                        )}>
                          <Power className={cn(
                            "w-6 h-6 transition-colors duration-500",
                            safeMode ? 'text-green-600' : colors.textAccent
                          )} />
                        </div>
                        <div>
                          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
                            Safe Mode
                          </h3>
                          <p className={cn("text-sm", colors.textSecondary)}>
                            {safeMode 
                              ? 'Professional theme with corporate AI' 
                              : 'Full customization unlocked'
                            }
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleSafeModeToggle}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300",
                          safeMode
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : cn(colors.bgActive, colors.textAccent, 'border', colors.borderSecondary)
                        )}
                      >
                        <span>{safeMode ? 'ON' : 'OFF'}</span>
                        <div className={`w-10 h-5 rounded-full relative transition-all duration-300 ${
                          safeMode ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
                            safeMode ? 'left-0.5' : 'left-5'
                          }`} />
                        </div>
                      </button>
                    </div>
                    <p className={cn("text-xs", colors.textMuted)}>
                      Safe Mode enables professional AI responses suitable for enterprise environments
                    </p>
                  </Card>

                  {/* AI Personality Section */}
                  <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                    <div className="flex items-center gap-3 mb-2">
                      <Brain className={cn("w-5 h-5", colors.textAccent)} />
                      <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>AI Personality</h3>
                    </div>
                    <p className={cn("text-sm mb-4", colors.textMuted)}>
                      {safeMode 
                        ? 'Safe Mode is ON - AI uses professional enterprise voice'
                        : 'Choose how your AI Co-Pilot communicates'
                      }
                    </p>
                    
                    {/* Current Personality Display */}
                    <div className={cn("p-4 rounded-xl border mb-4", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{activePersonality.icon}</span>
                        <span className={cn("font-semibold", colors.textAccent)}>
                          {activePersonality.name}
                        </span>
                        <span className={cn("text-xs px-2 py-0.5 rounded-full", colors.accentSolid, "text-white")}>
                          Active
                        </span>
                      </div>
                      <p className={cn("text-sm", colors.textSecondary)}>{activePersonality.description}</p>
                      <p className={cn("text-sm italic mt-2", colors.textMuted)}>
                        &ldquo;{activePersonality.preview}&rdquo;
                      </p>
                    </div>
                    
                    {/* Personality Selector (only when Safe Mode OFF) */}
                    {!safeMode && (
                      <div className="grid gap-3">
                        {selectablePersonalities.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => handlePersonalityChange(p.id)}
                            className={cn(
                              "p-4 rounded-xl text-left transition-all duration-300 border",
                              personality === p.id
                                ? cn(colors.bgActive, 'border-2', colors.borderAccent)
                                : cn(colors.bgTertiary, colors.borderPrimary, colors.bgHover)
                            )}
                          >
                            <div className="flex items-start gap-4">
                              <span className="text-3xl">{p.icon}</span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={cn("font-semibold", colors.textPrimary)}>{p.name}</span>
                                  {personality === p.id && (
                                    <span className={cn("text-xs px-2 py-0.5 rounded-full", colors.accentSolid, "text-white")}>
                                      Active
                                    </span>
                                  )}
                                </div>
                                <p className={cn("text-sm mb-1", colors.textSecondary)}>{p.description}</p>
                                <p className={cn("text-xs italic", colors.textMuted)}>&ldquo;{p.preview}&rdquo;</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
              </TabsContent>

              {/* Profile Tab */}
              <TabsContent value="profile">
                <div className="space-y-6">
                <Collapsible defaultOpen={true}>
                <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                  <CollapsibleTrigger asChild>
                    <div className={cn("group w-full p-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg")}>
                      <div className="flex items-center gap-3">
                        <User className={cn("w-5 h-5", colors.textAccent)} />
                        <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Profile Information</h3>
                      </div>
                      <ChevronDown className={cn("w-5 h-5 transition-transform duration-200 group-data-[state=closed]:-rotate-90", colors.textMuted)} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                  <div className="px-6 pb-6 space-y-6">
                    <div className="flex items-center gap-4">
                      {user?.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user?.name}
                          className={cn("w-20 h-20 rounded-full object-cover ring-2", colors.ringColor)}
                        />
                      ) : (
                        <div className={cn("w-20 h-20 rounded-full ring-2 flex items-center justify-center", colors.ringColor, colors.logoGradient)}>
                          <span className="text-2xl font-bold text-white">
                            {user?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                      )}
                      <Button 
                        variant="outline" 
                        className={cn("border", colors.borderAccent, colors.textAccent)}
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        data-testid="change-avatar-btn"
                      >
                        {uploadingAvatar ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Uploading...
                          </>
                        ) : (
                          'Change Avatar'
                        )}
                      </Button>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarUpload}
                      />
                    </div>

                    <div>
                      <Label htmlFor="name" className={colors.textSecondary}>Full Name</Label>
                      <Input
                        id="name"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className={colors.textSecondary}>Email</Label>
                      <Input
                        id="email"
                        type="email"
                        defaultValue={user?.email}
                        disabled
                        className={cn("mt-2", colors.bgTertiary, colors.borderPrimary, colors.textMuted)}
                      />
                      <p className={cn("text-xs mt-1", colors.textMuted)}>Email cannot be changed</p>
                    </div>

                    <div>
                      <Label htmlFor="timezone" className={colors.textSecondary}>Timezone</Label>
                      <select
                        id="timezone"
                        value={userTimezone}
                        onChange={(e) => setUserTimezone(e.target.value)}
                        className={cn("mt-2 w-full h-10 px-3 rounded-md border", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                      >
                        <optgroup label="Common Timezones">
                          {availableTimezones.common?.map(tz => (
                            <option key={tz.id} value={tz.id}>{tz.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="All Timezones">
                          {availableTimezones.all?.map(tz => (
                            <option key={tz} value={tz}>{tz}</option>
                          ))}
                        </optgroup>
                      </select>
                      <p className={cn("text-xs mt-1", colors.textMuted)}>
                        Used for displaying dates and times. Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                      </p>
                    </div>

                    <Button 
                      onClick={handleSaveProfile} 
                      disabled={saving}
                      className={cn(colors.accentGradient, colors.accentGradientHover)}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                  </CollapsibleContent>
                </Card>
                </Collapsible>

                {/* Email Notifications */}
                {!isBillingAdmin && (
                <Collapsible defaultOpen={true}>
                <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                  <CollapsibleTrigger asChild>
                    <div className={cn("group w-full p-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg")}>
                      <div className="flex items-center gap-3">
                        <Mail className={cn("w-5 h-5", colors.textAccent)} />
                        <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Email Notifications</h3>
                      </div>
                      <ChevronDown className={cn("w-5 h-5 transition-transform duration-200 group-data-[state=closed]:-rotate-90", colors.textMuted)} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                  <p className={cn("text-sm mb-6 px-6", colors.textMuted)}>
                    Choose which notifications you want to receive via email. In-app notifications (bell icon) are always enabled.
                  </p>
                  
                  <div className="space-y-4 px-6 pb-6">
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <CreditCard className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>Subscription Alerts</p>
                          <p className={cn("text-sm", colors.textMuted)}>Payment failures, plan expiring, billing updates</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_subscription_alerts}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_subscription_alerts', checked)}
                      />
                    </div>
                    
                    {isEnabled(FEATURE_IDS.TAB_IDEATION) && (
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <Sparkles className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>AI Processing Complete</p>
                          <p className={cn("text-sm", colors.textMuted)}>When long-running AI tasks finish</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_ai_complete}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_ai_complete', checked)}
                      />
                    </div>
                    )}
                    
                    {isEnabled(FEATURE_IDS.TAB_IDEATION) && (
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <FileText className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>Research Complete</p>
                          <p className={cn("text-sm", colors.textMuted)}>When deep research reports finish</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_research_complete ?? true}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_research_complete', checked)}
                      />
                    </div>
                    )}
                    
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <AtSign className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>@Mentions</p>
                          <p className={cn("text-sm", colors.textMuted)}>When someone mentions you in notes</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_mentions}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_mentions', checked)}
                      />
                    </div>
                    
                    {isEnabled(FEATURE_IDS.ORG_TEAM) && (
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <UserPlus className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>Feature Assignments</p>
                          <p className={cn("text-sm", colors.textMuted)}>When you are assigned to a feature</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_feature_assigned}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_feature_assigned', checked)}
                      />
                    </div>
                    )}
                    
                    <div className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                      <div className="flex items-center gap-3">
                        <GitBranch className={cn("w-5 h-5", colors.textAccent)} />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>Kanban Updates</p>
                          <p className={cn("text-sm", colors.textMuted)}>When features move columns (based on kanban rules)</p>
                        </div>
                      </div>
                      <Switch
                        checked={notifPrefs.email_kanban_updates}
                        onCheckedChange={(checked) => handleNotifPrefChange('email_kanban_updates', checked)}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6 px-6 pb-6">
                    <Button 
                      onClick={handleSaveNotificationPrefs} 
                      disabled={savingNotifications}
                      className={cn(colors.accentGradient, colors.accentGradientHover)}
                    >
                      {savingNotifications ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Notification Preferences'
                      )}
                    </Button>
                  </div>
                  </CollapsibleContent>
                </Card>
                </Collapsible>
                )}

                {/* Connected Accounts - only show if ADO integration is enabled */}
                {isEnabled(FEATURE_IDS.INTEGRATION_ADO) && (
                  <ConnectedAccountsSection colors={colors} user={user} />
                )}
                </div>
              </TabsContent>

              {/* Organization Tab */}
              <TabsContent value="organization">
                <div className="space-y-6">
                  {/* Company Information Card - Collapsible */}
                  <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                    <button
                      onClick={() => setCompanyInfoExpanded(!companyInfoExpanded)}
                      className={cn(
                        "w-full p-6 flex items-center justify-between text-left",
                        "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className={cn("w-5 h-5", colors.textAccent)} />
                        <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Company Information</h3>
                        {!canEditOrganization && (
                          <span className={cn("text-xs px-2 py-1 rounded ml-2", colors.bgActive, colors.textAccent)}>
                            Owner access required to edit
                          </span>
                        )}
                      </div>
                      <ChevronDown className={cn(
                        "w-5 h-5 transition-transform duration-200",
                        colors.textMuted,
                        companyInfoExpanded ? "rotate-180" : ""
                      )} />
                    </button>
                    
                    {companyInfoExpanded && (
                      <div className="px-6 pb-6 space-y-6">
                        <div>
                          <Label className={cn("mb-3 block", colors.textSecondary)}>Company Logo</Label>
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              "w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden",
                              colors.borderPrimary,
                              colors.bgTertiary
                            )}>
                              {organization?.logo_url ? (
                                <img src={organization.logo_url.startsWith('/') ? `${BACKEND_URL}${organization.logo_url}` : organization.logo_url} alt="Company logo" className="w-full h-full object-contain rounded-xl" />
                              ) : (
                                <Building2 className={cn("w-10 h-10", colors.textMuted)} />
                              )}
                            </div>
                            {canEditOrganization && (
                              <div>
                                <input
                                  ref={logoInputRef}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={handleLogoUpload}
                                  data-testid="logo-file-input"
                                />
                                <Button
                                  variant="outline"
                                  className={cn("mb-2 border", colors.borderPrimary)}
                                  onClick={() => logoInputRef.current?.click()}
                                  disabled={uploadingLogo}
                                  data-testid="upload-logo-button"
                                >
                                  {uploadingLogo ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                                </Button>
                                <p className={cn("text-xs", colors.textMuted)}>PNG, JPG up to 2MB</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="companyName" className={colors.textSecondary}>
                            <Building2 className="w-4 h-4 inline mr-2" />
                            Company Name
                          </Label>
                          <Input
                            id="companyName"
                            value={orgForm.name}
                            onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                            disabled={!canEditOrganization}
                            placeholder="Acme Inc."
                            className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="companyWebsite" className={colors.textSecondary}>
                            <Globe className="w-4 h-4 inline mr-2" />
                            Website
                          </Label>
                          <Input
                          id="companyWebsite"
                          value={orgForm.company_website}
                          onChange={(e) => setOrgForm({ ...orgForm, company_website: e.target.value })}
                          disabled={!canEditOrganization}
                          placeholder="https://example.com"
                          className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                        />
                      </div>

                      <div>
                        <Label className={colors.textSecondary}>
                          <Briefcase className="w-4 h-4 inline mr-2" />
                          Industry
                        </Label>
                        <Select 
                          value={orgForm.industry} 
                          onValueChange={(value) => setOrgForm({ ...orgForm, industry: value })}
                          disabled={!canEditOrganization}
                        >
                          <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            {INDUSTRIES.map((ind) => (
                              <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className={colors.textSecondary}>
                          <Users className="w-4 h-4 inline mr-2" />
                          Team Size
                        </Label>
                        <Select 
                          value={orgForm.team_size} 
                          onValueChange={(value) => setOrgForm({ ...orgForm, team_size: value })}
                          disabled={!canEditOrganization}
                        >
                          <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select team size" />
                          </SelectTrigger>
                          <SelectContent>
                            {TEAM_SIZES.map((size) => (
                              <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Timezone */}
                      <div>
                        <Label className={colors.textSecondary}>
                          <Clock className="w-4 h-4 inline mr-2" />
                          Organization Timezone
                        </Label>
                        <Select 
                          value={orgForm.timezone} 
                          onValueChange={(value) => setOrgForm({ ...orgForm, timezone: value })}
                          disabled={!canEditOrganization}
                        >
                          <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                            <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                            <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                            <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                            <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                            <SelectItem value="Pacific/Honolulu">Hawaii Time (HT)</SelectItem>
                            <SelectItem value="UTC">UTC</SelectItem>
                            <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                            <SelectItem value="Europe/Paris">Central European (CET)</SelectItem>
                            <SelectItem value="Europe/Berlin">Berlin (CET)</SelectItem>
                            <SelectItem value="Asia/Dubai">Dubai (GST)</SelectItem>
                            <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                            <SelectItem value="Asia/Singapore">Singapore (SGT)</SelectItem>
                            <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                            <SelectItem value="Australia/Sydney">Sydney (AEST)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className={cn("text-xs mt-1", colors.textMuted)}>
                          Used for sprint auto-start/close scheduling
                        </p>
                      </div>

                      {canEditOrganization && (
                        <Button 
                          onClick={handleSaveOrganization} 
                          disabled={saving}
                          className={cn(colors.accentGradient, colors.accentGradientHover)}
                        >
                          {saving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save Organization Settings'
                          )}
                        </Button>
                      )}
                      </div>
                    )}
                  </Card>
                  
                  {/* Navigation Defaults Card - Owners Only, hidden for free_product */}
                  {isOrgOwner && !isFreeProduct && (
                    <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                      <button
                        onClick={() => setNavigationExpanded(!navigationExpanded)}
                        className={cn(
                          "w-full p-6 flex items-center justify-between text-left",
                          "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-lg"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Compass className={cn("w-5 h-5", colors.textAccent)} />
                          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Product Navigation Defaults</h3>
                        </div>
                        <ChevronDown className={cn(
                          "w-5 h-5 transition-transform duration-200",
                          colors.textMuted,
                          navigationExpanded ? "rotate-180" : ""
                        )} />
                      </button>
                      
                      {navigationExpanded && (
                        <div className="px-6 pb-6">
                          <OrgNavigationSettings 
                            organization={organization}
                            onSave={(data) => organizationsApi.updateMyOrg(data).then(() => refreshAuth())}
                            canEdit={true}
                          />
                        </div>
                      )}
                    </Card>
                  )}

                </div>
              </TabsContent>
              <TabsContent value="integrations">
                <OrgIntegrationsSection 
                  colors={colors} 
                  canEdit={canEditOrganization}
                  enabledIntegrations={{
                    github: isEnabled(FEATURE_IDS.INTEGRATION_GITHUB),
                    jira: isEnabled(FEATURE_IDS.INTEGRATION_JIRA),
                    linear: isEnabled(FEATURE_IDS.INTEGRATION_LINEAR),
                    azure_devops: isEnabled(FEATURE_IDS.INTEGRATION_ADO),
                  }}
                />
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team">
                <TeamPage embedded={true} />
              </TabsContent>

              {/* API Keys Tab */}
              <TabsContent value="api-keys">
                <DeveloperKeysTab />
              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing">
                <div className="space-y-6">
                  {/* Unified Pricing Grid - hidden for free_product users (they upgrade from product Upgrade tab) */}
                  {!isFreeProduct && (
                    <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                      <BillingPricingGrid onPlanChange={() => {
                        refreshAuth();
                        fetchSeatInfo();
                        setTokenRefreshKey(prev => prev + 1);
                      }} />
                    </Card>
                  )}

                  {/* Token Management - hidden for free_product users */}
                  {!isFreeProduct && (
                    <TokenManagement userRole={currentUserRole} refreshKey={tokenRefreshKey} />
                  )}

                  {/* Products & Features Usage Management */}
                  <UsageManagement refreshKey={tokenRefreshKey} />

                  {/* Seat Management Section - Only show for paid users */}
                  {(organization?.plan_tier === 'paid' || organization?.plan_tier === 'pro') && (
                    <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
                          <Users className="w-5 h-5" />
                          Seat Management
                        </h3>
                        <Button
                          onClick={() => {
                            const atLimit = seatInfo?.plan_seat_limit && 
                              (seatInfo.seats_total || 0) >= seatInfo.plan_seat_limit;
                            if (atLimit) {
                              setSeatLimitModalOpen(true);
                            } else {
                              handleOpenUpgradeModal();
                            }
                          }}
                          disabled={purchasingSeats}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="add-seats-btn"
                        >
                          {purchasingSeats ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          Add Seats
                        </Button>
                      </div>
                        
                      {loadingSeats ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                          </div>
                        ) : seatInfo ? (
                          <div className="space-y-4">
                            {/* Seat Usage */}
                            <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={cn("text-sm font-medium", colors.textSecondary)}>
                                  Seat Usage
                                </span>
                                <span className={cn("text-sm font-medium", colors.textPrimary)}>
                                  {seatInfo.seats_occupied || 0} / {seatInfo.seats_total || 0} seats occupied
                                </span>
                              </div>
                              <Progress 
                                value={seatInfo.seats_total > 0 
                                  ? ((seatInfo.seats_occupied || 0) / seatInfo.seats_total) * 100 
                                  : 0} 
                                className="h-2"
                              />
                              <div className="flex items-center justify-between mt-2">
                                <span className={cn("text-xs", colors.textMuted)}>
                                  {seatInfo.seats_available || 0} seat{(seatInfo.seats_available || 0) !== 1 ? 's' : ''} available
                                </span>
                                <span className={cn("text-xs", colors.textMuted)}>
                                  ${seatInfo.seat_price}/seat/month
                                </span>
                              </div>
                              
                              {/* Detailed seat breakdown */}
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className={colors.textMuted}>Purchased (billed):</span>
                                  <span className={colors.textPrimary}>{seatInfo.seats_purchased || 0}</span>
                                </div>
                                {(seatInfo.seats_comped || 0) > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className={colors.textMuted}>Comped (free):</span>
                                    <span className="text-green-600">{seatInfo.seats_comped}</span>
                                  </div>
                                )}
                                {seatInfo.plan_seat_limit && (
                                  <div className="flex justify-between text-xs">
                                    <span className={colors.textMuted}>Plan limit:</span>
                                    <span className={colors.textPrimary}>{seatInfo.plan_seat_limit} max</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Plan limit warning */}
                            {seatInfo.plan_seat_limit && (seatInfo.seats_total || 0) >= seatInfo.plan_seat_limit && (
                              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                                <p className="text-xs text-amber-800 dark:text-amber-200">
                                  <strong>Plan limit reached.</strong> {organization?.manual_billing
                                    ? 'Contact your administrator to add more seats.'
                                    : 'Upgrade your plan to add more seats.'}
                                </p>
                              </div>
                            )}
                            
                            {/* Seat Breakdown */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                                <div className="flex items-center gap-2 mb-1">
                                  <Crown className="w-4 h-4 text-amber-500" />
                                  <span className={cn("text-sm font-medium", colors.textPrimary)}>Paid Seats</span>
                                </div>
                                <p className={cn("text-xs", colors.textMuted)}>Owners & Product Managers</p>
                              </div>
                              <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                                <div className="flex items-center gap-2 mb-1">
                                  <Users className="w-4 h-4 text-green-500" />
                                  <span className={cn("text-sm font-medium", colors.textPrimary)}>Free Roles</span>
                                </div>
                                <p className={cn("text-xs", colors.textMuted)}>Collaborators & Billing Admins</p>
                              </div>
                            </div>
                            
                            {/* Pending Upgrade Requests */}
                            {pendingRequests.length > 0 && (
                              <div className={cn("p-4 rounded-lg border-2 border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20")}>
                                <div className="flex items-center gap-2 mb-3">
                                  <AlertTriangle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                  <span className={cn("font-medium text-purple-700 dark:text-purple-300")}>
                                    {pendingRequests.length} Team Member{pendingRequests.length !== 1 ? 's' : ''} Waiting for Seats
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {pendingRequests.map((req) => (
                                    <div 
                                      key={req.request_id || req.created_at}
                                      className={cn("flex items-center justify-between p-3 rounded-lg", colors.bgCard)}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-sm font-medium text-purple-700 dark:text-purple-300">
                                          {(req.requester_name || req.requester_email || '?')[0].toUpperCase()}
                                        </div>
                                        <div>
                                          <p className={cn("text-sm font-medium", colors.textPrimary)}>
                                            {req.requester_name || req.requester_email}
                                          </p>
                                          <p className={cn("text-xs flex items-center gap-1", colors.textMuted)}>
                                            <Clock className="w-3 h-3" />
                                            {req.limit_type === 'products' ? 'Needs more products' : 
                                             req.limit_type === 'features' ? 'Needs more features' :
                                             req.limit_type === 'seats' ? 'Needs seat assignment' : 'Upgrade requested'}
                                          </p>
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleResolveRequest(req.request_id)}
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      >
                                        Mark Resolved
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                                {seatInfo.available_seats < pendingRequests.length && (
                                  <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                                    <p className={cn("text-sm text-purple-700 dark:text-purple-300 mb-2")}>
                                      You need {pendingRequests.length - seatInfo.available_seats} more seat{pendingRequests.length - seatInfo.available_seats !== 1 ? 's' : ''} to fulfill all requests.
                                    </p>
                                    <Button
                                      onClick={() => handlePurchaseSeats(pendingRequests.length - seatInfo.available_seats)}
                                      disabled={purchasingSeats}
                                      className="bg-purple-600 hover:bg-purple-700 text-white"
                                    >
                                      {purchasingSeats ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                      ) : (
                                        <Plus className="w-4 h-4 mr-2" />
                                      )}
                                      Purchase {pendingRequests.length - seatInfo.available_seats} Seat{pendingRequests.length - seatInfo.available_seats !== 1 ? 's' : ''}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            
                          </div>
                        ) : (
                          <div className={cn("text-center py-6", colors.textMuted)}>
                            <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p>Unable to load seat information</p>
                          </div>
                        )}
                      </Card>
                  )}

                  {/* Payment Method Management */}
                  <PaymentMethodManagement refreshKey={tokenRefreshKey} />

                  {/* Billing Audit Log - Comprehensive History */}
                  <BillingAuditLog />
                </div>
              </TabsContent>
            </Tabs>

            {/* Close Organization Modal */}

            {showCloseModal && (
              <div className="fixed inset-0 z-50">
                <div className="fixed inset-0 bg-black/70" onClick={() => setShowCloseModal(false)} />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                  <Card className={cn("w-full max-w-lg p-6", colors.bgCard)}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-full bg-red-500/20">
                        <AlertTriangle className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                          Close Organization
                        </h2>
                        <p className={cn("text-sm", colors.textMuted)}>
                          This action is irreversible after 30 days
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                          You are about to close <strong>{organization?.name}</strong>
                        </p>
                        <ul className="text-xs text-red-700 dark:text-red-300 mt-2 space-y-1 list-disc list-inside">
                          <li>All team members will be notified and lose access</li>
                          <li>All products will be archived</li>
                          <li>Data will be permanently deleted in 30 days</li>
                        </ul>
                      </div>

                      <div>
                        <Label className={colors.textSecondary}>
                          Type <strong>{organization?.name}</strong> to confirm
                        </Label>
                        <Input
                          value={closeConfirmName}
                          onChange={(e) => setCloseConfirmName(e.target.value)}
                          placeholder={organization?.name}
                          className={cn("mt-2", colors.bgInput, colors.borderPrimary)}
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 dark:bg-gray-900">
                        <div>
                          <Label className={cn("font-medium", colors.textPrimary)}>
                            Cancel subscription immediately
                          </Label>
                          <p className={cn("text-xs", colors.textMuted)}>
                            Receive a prorated refund. Otherwise, subscription ends at billing period.
                          </p>
                        </div>
                        <Switch
                          checked={cancelImmediately}
                          onCheckedChange={setCancelImmediately}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowCloseModal(false);
                          setCloseConfirmName('');
                        }}
                        className={cn("border", colors.borderPrimary)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCloseOrganization}
                        disabled={closingOrg || closeConfirmName.toLowerCase().trim() !== organization?.name?.toLowerCase().trim()}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {closingOrg ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Closing...</>
                        ) : (
                          <><Trash2 className="w-4 h-4 mr-2" /> Close Organization</>
                        )}
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Seat Limit Reached Modal */}
      <Dialog open={seatLimitModalOpen} onOpenChange={setSeatLimitModalOpen}>
        <DialogContent className={cn("max-w-sm", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
              <Users className="w-5 h-5 text-amber-500" />
              Seat Limit Reached
            </DialogTitle>
            <DialogDescription className="pt-2">
              {organization?.manual_billing ? (
                <>Your organization is on a manually managed plan. To add more seats, please contact your account administrator.</>
              ) : (
                <>You've reached the maximum seats for your current plan. Upgrade your plan to unlock additional seats.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeatLimitModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal - Seat Selection */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
              <Sparkles className="w-5 h-5 text-purple-500" />
              {upgradeInfo?.isAlreadyPaid ? 'Add Seats' : 'Upgrade to Pro'}
            </DialogTitle>
            <DialogDescription>
              {upgradeInfo?.isAlreadyPaid 
                ? 'Purchase additional seats for your team'
                : 'Select the number of seats for your team'
              }
            </DialogDescription>
          </DialogHeader>
          
          {loadingUpgradeInfo ? (
            <div className="py-8 flex justify-center">
              <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* Current Usage Info */}
              {(upgradeInfo?.usedSeats > 0 || upgradeInfo?.pendingRequests > 0) && (
                <div className={cn("p-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20")}>
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      {upgradeInfo.isAlreadyPaid ? (
                        <p className="font-medium text-blue-800 dark:text-blue-300">
                          You have {upgradeInfo.usedSeats} of {upgradeInfo.seatsTotal} seat{upgradeInfo.seatsTotal !== 1 ? 's' : ''} in use
                          {upgradeInfo.seatsComped > 0 && (
                            <span className="text-green-600 dark:text-green-400 text-xs ml-1">
                              (incl. {upgradeInfo.seatsComped} comped)
                            </span>
                          )}
                          {upgradeInfo.pendingRequests > 0 && (
                            <span className="text-amber-700 dark:text-amber-400">
                              {' '}+ {upgradeInfo.pendingRequests} waiting
                            </span>
                          )}
                        </p>
                      ) : (
                        <>
                          <p className="font-medium text-blue-800 dark:text-blue-300">
                            {upgradeInfo.usedSeats > 0 && (
                              <span>{upgradeInfo.usedSeats} Owner{upgradeInfo.usedSeats > 1 ? 's' : ''}/PM{upgradeInfo.usedSeats > 1 ? 's' : ''}</span>
                            )}
                            {upgradeInfo.usedSeats > 0 && upgradeInfo.pendingRequests > 0 && ' + '}
                            {upgradeInfo.pendingRequests > 0 && (
                              <span>{upgradeInfo.pendingRequests} team member{upgradeInfo.pendingRequests > 1 ? 's' : ''} waiting to be upgraded</span>
                            )}
                            {upgradeInfo.usedSeats > 0 && !upgradeInfo.pendingRequests && ' need paid seats'}
                          </p>
                          <p className="text-blue-700 dark:text-blue-400 text-xs mt-1">
                            {upgradeInfo.pendingRequests > 0 
                              ? `We recommend ${upgradeInfo.usedSeats + upgradeInfo.pendingRequests} seats to cover your team.`
                              : 'Collaborators don\'t require seats and remain free.'
                            }
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Plan Limit Warning - Show when at limit */}
              {upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
               upgradeInfo.seatsTotal >= upgradeInfo.planSeatLimit && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Plan Seat Limit Reached
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Your plan allows a maximum of {upgradeInfo.planSeatLimit} seats. 
                        You currently have {upgradeInfo.seatsTotal} seat{upgradeInfo.seatsTotal !== 1 ? 's' : ''}.
                        Contact support to upgrade your plan limit.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Seat Selector */}
              <div className={cn("p-4 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                <Label className={cn("text-sm font-medium mb-2 block", colors.textSecondary)}>
                  {upgradeInfo?.isAlreadyPaid ? 'Additional Seats' : 'Number of Seats'}
                </Label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUpgradeSeats(Math.max(1, upgradeSeats - 1))}
                    disabled={upgradeSeats <= 1}
                    className={cn("w-10 h-10", colors.borderPrimary)}
                  >
                    -
                  </Button>
                  <div className={cn("w-16 h-10 flex items-center justify-center rounded-md border text-lg font-semibold", colors.bgCard, colors.borderPrimary, colors.textPrimary)}>
                    {upgradeSeats}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUpgradeSeats(upgradeSeats + 1)}
                    disabled={upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
                              (upgradeInfo.seatsTotal + upgradeSeats) >= upgradeInfo.planSeatLimit}
                    className={cn("w-10 h-10", colors.borderPrimary, 
                      upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
                      (upgradeInfo.seatsTotal + upgradeSeats) >= upgradeInfo.planSeatLimit && 
                      "opacity-50 cursor-not-allowed")}
                  >
                    +
                  </Button>
                </div>
                <p className={cn("text-xs mt-2", colors.textMuted)}>
                  Each seat allows 1 Owner or Product Manager. Collaborators are free.
                  {upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && (
                    <span className="block mt-1 text-amber-600">
                      Plan limit: {upgradeInfo.planSeatLimit} seats max
                    </span>
                  )}
                </p>
              </div>
              
              {/* Price Summary */}
              <div className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                <div className="flex justify-between items-center mb-2">
                  <span className={cn("text-sm", colors.textSecondary)}>Price per seat</span>
                  <span className={cn("font-medium", colors.textPrimary)}>${upgradeInfo?.pricePerSeat || 0}/month</span>
                </div>
                {upgradeInfo?.isAlreadyPaid && (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className={cn("text-sm", colors.textSecondary)}>Purchased seats (billed)</span>
                      <span className={cn("font-medium", colors.textPrimary)}>{upgradeInfo.purchasedSeats}</span>
                    </div>
                    {upgradeInfo.seatsComped > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className={cn("text-sm", colors.textSecondary)}>Comped seats (free)</span>
                        <span className={cn("font-medium text-green-600")}>{upgradeInfo.seatsComped}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between items-center mb-2">
                  <span className={cn("text-sm", colors.textSecondary)}>
                    {upgradeInfo?.isAlreadyPaid ? 'Adding' : 'Seats selected'}
                  </span>
                  <span className={cn("font-medium", colors.textPrimary)}>+ {upgradeSeats}</span>
                </div>
                <div className={cn("border-t pt-2 mt-2 flex justify-between items-center", colors.borderPrimary)}>
                  <span className={cn("font-semibold", colors.textPrimary)}>
                    {upgradeInfo?.isAlreadyPaid ? 'Additional cost' : 'Total'}
                  </span>
                  <span className="text-xl font-bold text-purple-600">
                    ${upgradeSeats * (upgradeInfo?.pricePerSeat || 0)}/month
                  </span>
                </div>
                {upgradeInfo?.isAlreadyPaid && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed">
                    <span className={cn("text-sm", colors.textSecondary)}>New total seats</span>
                    <span className={cn("font-semibold", colors.textPrimary)}>
                      {upgradeInfo.seatsTotal + upgradeSeats} seats
                    </span>
                  </div>
                )}
              </div>
              
              {/* Benefits - Only show for new upgrades */}
              {!upgradeInfo?.isAlreadyPaid && (
                <div className="space-y-2">
                  <p className={cn("text-xs font-medium", colors.textMuted)}>Included with Pro:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className={colors.textSecondary}>
                        {upgradeInfo?.planFeatures?.maxProducts || 'Unlimited'} products
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className={colors.textSecondary}>
                        {upgradeInfo?.planFeatures?.maxFeatures || 'Unlimited'} features
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className={colors.textSecondary}>Advanced AI</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className={colors.textSecondary}>Priority support</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Tip for adding seats */}
              {upgradeInfo?.isAlreadyPaid && pendingRequests.length > 0 && (
                <div className={cn("p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20")}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      You have {pendingRequests.length} pending upgrade request{pendingRequests.length > 1 ? 's' : ''} from team members.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className={cn(colors.borderPrimary)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartUpgrade}
              disabled={startingUpgrade || loadingUpgradeInfo || 
                (upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
                 upgradeInfo.seatsTotal >= upgradeInfo.planSeatLimit)}
              className={cn(
                "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white",
                (upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
                 upgradeInfo.seatsTotal >= upgradeInfo.planSeatLimit) && "opacity-50 cursor-not-allowed"
              )}
            >
              {startingUpgrade ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : upgradeInfo?.isAlreadyPaid && upgradeInfo?.planSeatLimit && 
                   upgradeInfo.seatsTotal >= upgradeInfo.planSeatLimit ? (
                <>Plan Limit Reached</>
              ) : (
                <>Continue to Payment</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Assign Seats Modal - Post Checkout */}
      <AssignSeatsModal
        isOpen={showAssignSeats}
        onClose={() => setShowAssignSeats(false)}
        seatsToAssign={newSeatsCount}
        onAssigned={() => {
          fetchSeatInfo();
          setShowAssignSeats(false);
        }}
      />
    </div>
  );
};

export default SettingsPage;
